import React, { useState, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { OutlineButton } from '../../../components/ui/buttons/OutlineButton';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { Brain, BookOpen, Microscope } from 'lucide-react';
import { Card } from './Card';
import { ResultsGraph } from './ResultsGraph';

/**
 * Wason Selection Task Simulation for Confirmation Bias
 * 
 * This simulation recreates Wason's 1966 selection task demonstrating 
 * confirmation bias in logical reasoning. It shows how people tend to
 * verify rather than falsify hypotheses even when falsification is the
 * correct logical approach.
 */
const TemplatedConfirmationBiasSimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState<'intro' | 'task' | 'results'>('intro');
  const [selectedCards, setSelectedCards] = useState<number[]>([]);
  
  const simulationRef = useRef<HTMLDivElement>(null);

  // Cards data (E, K, 4, 7)
  const cards = [
    { id: 1, face: 'E', isSelected: false },
    { id: 2, face: 'K', isSelected: false },
    { id: 3, face: '4', isSelected: false },
    { id: 4, face: '7', isSelected: false }
  ];

  // ========== EVENT HANDLERS ==========
  const handleCardSelect = (id: number) => {
    setSelectedCards(prev => {
      if (prev.includes(id)) {
        return prev.filter(cardId => cardId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSubmit = () => {
    setStage('results');
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReset = () => {
    setStarted(false);
    setStage('intro');
    setSelectedCards([]);
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ========== RESULTS CALCULATIONS ==========
  const getResultMessage = () => {
    // Correct answer is cards 1 and 4 (E and 7)
    const hasCardE = selectedCards.includes(1);
    const hasCardK = selectedCards.includes(2);
    const hasCard4 = selectedCards.includes(3);
    const hasCard7 = selectedCards.includes(4);
    
    if (hasCardE && !hasCardK && !hasCard4 && hasCard7) {
      return {
        isCorrect: true,
        message: "Correct! You selected the right cards to test the rule. The E card (to check if it has an even number on the back) and the 7 card (to verify it doesn't have a vowel on the back) are the only ones that could potentially falsify the rule."
      };
    } else if (hasCardE && hasCard4 && !hasCardK && !hasCard7) {
      return {
        isCorrect: false,
        message: "You selected E and 4, a common choice that shows confirmation bias. While checking the E card is necessary, the 4 card cannot falsify the rule regardless of what's on its back. The 7 card is critical because finding a vowel on its back would disprove the rule."
      };
    } else if (selectedCards.length === 0) {
      return {
        isCorrect: false,
        message: "You didn't select any cards. To test the rule, you need to select cards that could potentially prove it false."
      };
    } else {
      return {
        isCorrect: false,
        message: "Your selection wasn't optimal for testing the rule. The E and 7 cards are the only ones that could potentially falsify the rule."
      };
    }
  };

  // For result charts
  const getStudyData = () => {
    return [
      { name: 'E card', value: 89, fill: '#f97316' },
      { name: 'K card', value: 16, fill: '#f97316' },
      { name: '4 card', value: 62, fill: '#f97316' },
      { name: '7 card', value: 25, fill: '#f97316' }
    ];
  };

  // ========== SIMULATION CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* Selection Task */}
      <SimulationStep
        title="The Wason Selection Task"
        isActive={stage === 'task'}
      >
        <div className="space-y-6">
          <Text className="text-gray-700">
            You are shown four cards. Each card has a letter on one side and a number on the other.
          </Text>
          <Text className="text-gray-700 font-medium">
            Rule: "If a card has a vowel on one side, then it has an even number on the other side."
          </Text>
          <Text className="text-gray-700">
            Which card(s) must you turn over to determine if the rule is being followed? Select all that apply.
          </Text>
          
          <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center items-center gap-3 sm:gap-4 my-6 sm:my-8 px-2 max-w-[280px] sm:max-w-[350px] md:max-w-none mx-auto">
            {cards.map((card, index) => (
              <div 
                key={card.id} 
                className="flex justify-center items-center transform transition-all duration-500 hover:scale-110 animate-fadeIn"
                style={{
                  animationDelay: `${index * 150}ms`,
                  opacity: 0,
                  animationFillMode: 'forwards',
                  transform: `rotate(${index % 2 === 0 ? -1 + index/2 : 1 - index/2}deg)`
                }}
              >
                <Card
                  face={card.face}
                  selected={selectedCards.includes(card.id)}
                  onClick={() => handleCardSelect(card.id)}
                  disabled={stage !== 'task'}
                />
              </div>
            ))}
          </div>
          
          {/* Selection feedback */}
          {selectedCards.length > 0 && (
            <Text className="text-center text-gray-600 italic">
              You've selected {selectedCards.length} card{selectedCards.length > 1 ? 's' : ''}. 
              {selectedCards.length < 4 ? ' You can select more cards if needed.' : ''}
            </Text>
          )}

          <div className="flex justify-center mt-4">
            <AccentButton 
              onClick={handleSubmit}
              disabled={stage !== 'task' || selectedCards.length === 0}
              className={`${selectedCards.length === 0 ? 'opacity-50' : 'opacity-100'} px-8`}
            >
              Check Answer
            </AccentButton>
          </div>
        </div>
      </SimulationStep>

      {/* Results */}
      {stage === 'results' && (
        <div className="space-y-6">
          {/* Understanding Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Step 1: User Results */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                
                <Text className="text-gray-700">
                  You selected: {selectedCards.map(id => cards.find(card => card.id === id)?.face).join(', ') || 'No cards'}
                </Text>
                
                <ResultMessage 
                  isCorrect={getResultMessage().isCorrect} 
                  message={getResultMessage().message}
                />
              </div>
              
              {/* Step 2: Connection to the Bias */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to Confirmation Bias
                </Text>
                <Text className="text-gray-700">
                  This task demonstrates how we tend to seek confirming evidence rather than disconfirming evidence. 
                  Logically, the rule "If vowel, then even number" is only violated when there is a vowel with an odd number.
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>The E card must be checked - if it has an odd number on the back, the rule is broken</li>
                  <li>The K card doesn't need to be checked - the rule says nothing about consonants</li>
                  <li>The 4 card doesn't need to be checked - even if it has a consonant, the rule isn't violated</li>
                  <li>The 7 card must be checked - if it has a vowel on the back, the rule is broken</li>
                </ul>
                <Text className="text-gray-700">
                  The tendency to select E and 4 cards (which most people do) reveals confirmation bias at work. People naturally look for evidence that confirms the rule rather than trying to falsify it, even though falsification is the correct logical approach.
                </Text>
              </div>
              
              {/* Step 3: Study Findings */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  In Wason's 1966 study, only about 10% of participants solved the problem correctly. The results showed clear patterns:
                </Text>
                
                <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                  <li>89% of participants selected the E card (which is correct)</li>
                  <li>62% selected the 4 card (which is unnecessary)</li>
                  <li>Only 25% selected the 7 card (which is essential)</li>
                  <li>16% selected the K card (which is unnecessary)</li>
                </ul>
                
                <GraphContainer>
                  <div className="h-[340px]">
                    <ResultsGraph studyData={getStudyData()} />
                  </div>
                </GraphContainer>
                
                <Text className="text-gray-700">
                  This experiment reveals a fundamental tendency in human thinking - we naturally look for evidence that confirms 
                  our existing beliefs or hypotheses rather than evidence that might disprove them. This is despite the fact that 
                  in logic and science, falsification is often more powerful than confirmation.
                </Text>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock 
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by Peter Wason (1966), "Reasoning about a rule," published in the Quarterly Journal of Experimental Psychology. 
                This classic study demonstrated how people struggle with logical falsification tasks and tend to seek confirming rather than 
                disconfirming evidence.
              </Text>
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title="Confirmation Bias: The Wason Selection Task"
      description="This simulation recreates Wason's classic 1966 card selection task that demonstrates confirmation bias in logical reasoning. It reveals how we tend to seek confirmatory evidence rather than disconfirming evidence, even when falsification is the best way to test a hypothesis."
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => {
            setStarted(true);
            setStage('task');
          }}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default TemplatedConfirmationBiasSimulation; 